import { type ECharts, type EChartsInitOpts, type SetOptionOpts } from 'echarts';
import { util, init as echartsInit } from 'echarts/core';
import { version } from '../../package.json';
import EventHandler from './EventHandler';
import Model from './Model';
import { debug } from '../util/debug';
import { type PresetTheme } from '../theme';
import type { Option } from './option';

export default class StandardChart extends EventHandler {
  /** 埋点统计的ID */
  protected readonly __statisticId = 'thsc-datav-standard-chart';

  protected _$dom: HTMLElement;

  protected _initOption: EChartsInitOpts;

  private __echarts: ECharts;

  /** 配置项管理 */
  protected _model: Model;

  protected _initialized: boolean;

  private __pendingCallback: (() => void)[];

  private __playOption: { opts?: SetOptionOpts } = {};

  /**
   * 构造函数
   * * 该阶段只完成所有变量初始化和组件的实例化，不执行任何繁重的计算逻辑
   */
  constructor(dom: HTMLElement, theme?: PresetTheme, initOption?: EChartsInitOpts) {
    super();
    debug('new Chart: %o', this);

    this._$dom = dom;
    this._$dom.style.userSelect = 'none';

    this._initOption = util.clone(initOption);
    this._model = new Model(theme);

    this.__pendingCallback = [];
    this._initialized = false;
    // * Why? see: https://nodejs.org/en/docs/guides/event-loop-timers-and-nexttick/#why-use-process-nexttick
    setTimeout(() => {
      this._initialize();
      this._initialized = true;

      this.__pendingCallback.forEach(cb => cb());
      this.__pendingCallback = [];
    }, 0);

    StandardChart.__statistic(this.__statisticId);
  }

  getDom() {
    return this._$dom;
  }

  getECharts() {
    return this.__echarts;
  }

  getModel() {
    return this._model;
  }

  protected _initialize() {
    debug('dv:beforeinit');
    const self = this;
    self.dispatchEvent('dv:beforeinit');

    // * 便于线上调调试

    self._$dom.__$$getStandardChartIns = () => self;

    self.__echarts = echartsInit(self._$dom, null, this._initOption);
    // ! 为了方便在写扩展时可以访问上层封装的对象，但应该尽可能避免使用

    self.__echarts.$dvGetOwner = () => self;

    self._flushPendingToRegisterEChartsInsEvents();
    self._bindEvents();

    self.dispatchEvent('dv:afterinit');
    debug('dv:afterinit');
  }

  /**
   * 销毁实例
   * * 释放资源引用，避免内存泄露
   */
  destroy() {
    if (!this._initialized) {
      return;
    }
    debug('dv:destroy...');
    this.dispatchEvent('dv:beforedestroy');

    delete this._$dom.__$$getStandardChartIns;

    this.__echarts.dispose();
    this.__echarts = null;
    this._initialized = false;

    this.dispatchEvent('dv:destroy');

    this.dispatchEvent('dv:afterdestroy');
  }

  // 动画 -----------------------------------------------------------------

  play(arg: { option: Option } & this['__playOption']) {
    if (!this._initialized) {
      this.__pendingCallback.push(() => {
        this.play(arg);
      });

      return this;
    }
    this.dispatchEvent('dv:play');

    const { option, ...playOption } = arg;
    if (typeof option === 'undefined') {
      throw new Error(`"option" is required.`);
    }
    this.__playOption = playOption;

    this._model.update(option);
    this.__update();

    return this;
  }

  private __update() {
    debug('dv:beforeupdate');
    this.dispatchEvent('dv:beforeupdate');

    this._model.prepare(this);
    const option = this._model.getOption();
    console.log('option::', option)
    this.__echarts.setOption(option, this.__playOption.opts);

    this.dispatchEvent('dv:afterupdate');
    debug('dv:afterupdate: %o', option);
  }

  /**
   * 发送埋点
   */
  private static __statistic(statisticId: string) {
    // if (!this.track) {
    //   return false;
    // }

    if (window._thsclog) {
      // 总量
      window._thsclog({
        name: statisticId,
        ver: 'all'
      });
      // 单版本量
      window._thsclog({
        name: statisticId,
        ver: version
      });
    } else {
      console.warn('埋点需要，请添加js依赖<script src="//s.thsi.cn/js/ta.min.js"></script>');
    }

    return null;
  }

  //
}
